import { arrow, computePosition, flip, offset, shift } from '@floating-ui/react';
import classNames from 'classnames';

import React, {
  FC,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cropText } from '../lib/crop-text';
import { Typography } from '../typography';

import styles from './tooltip.scss';

type Placement = 'top' | 'right' | 'bottom' | 'left';
type Event = 'click' | 'hover';

const DEFAULT_MAX_WIDTH = 328;

const COMPONENT_ID = 'modulor-tooltip';

const STATIC_SIDE = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

const PADDING_ARROW = {
  top: '-14px',
  right: '-26px',
  bottom: '-13px',
  left: '-26px',
};

type Props = RefAttributes<HTMLSpanElement> &
  HTMLAttributes<HTMLSpanElement> & {
    title?: string;
    text?: string;
    event?: Event;
    placement?: Placement;
    maxWidth?: number;
    heading: string;
    show?: boolean;
    maxTitleLength?: number;
    maxTextLength?: number;
    children?: ReadonlyArray<ReactElement>;
    button?: ReactElement;
    dataTestId?: string;
  };

export const Tooltip: FC<Props> = forwardRef<HTMLSpanElement, Props>(
  (
    {
      title,
      text,
      event = 'hover',
      placement = 'bottom',
      maxWidth,
      heading,
      show,
      children,
      maxTitleLength = 60,
      maxTextLength = 160,
      style,
      button,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const tooltip = useRef<HTMLDivElement>(null);
    const content = useRef<HTMLSpanElement>(null);
    const arrowElem = useRef<HTMLDivElement>(null);

    const updateTooltip = useCallback(() => {
      computePosition(content.current, tooltip.current, {
        placement: placement,
        middleware: [
          offset(21),
          shift({ padding: 4 }),
          arrow({ element: arrowElem }),
          flip({
            fallbackPlacements: ['right', 'bottom', 'top', 'left'],
          }),
        ],
      }).then(({ x, y, middlewareData }) => {
        let topValue = '0';
        if (placement === 'bottom') {
          topValue = '8px';
        } else if (placement === 'top') {
          topValue = '-8px';
        }

        Object.assign(tooltip.current.style, {
          left: '0',
          top: `${topValue}`,
          transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
        });
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        arrowElem.current.className = classNames([styles['arrow-elem'], styles[`${placement}`]]);

        Object.assign(arrowElem.current.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY < 17 ? 17 : arrowY}px` : '',
          right: '',
          bottom: '',
          [STATIC_SIDE[placement.split('-')[0]]]: PADDING_ARROW[placement],
        });
      });
    }, [placement, arrowElem]);

    const showTooltip = useCallback(() => {
      tooltip.current.style.opacity = '1';
      setIsVisible(true);
      updateTooltip();
    }, [tooltip, updateTooltip, setIsVisible]);

    const hideTooltip = useCallback(() => {
      tooltip.current.style.opacity = '0';
      setIsVisible(false);
    }, [tooltip, setIsVisible]);

    const toggleTooltip = useCallback(() => {
      if (isVisible) {
        hideTooltip();
        return;
      }
      showTooltip();
    }, [isVisible, hideTooltip, showTooltip]);

    const defaultEvents = useMemo(() => {
      return {
        onFocus: focus,
        onBlur: toggleTooltip,
      };
    }, [toggleTooltip]);

    const events = useMemo(() => {
      return {
        hover: {
          ...defaultEvents,
          onMouseEnter: showTooltip,
          onMouseLeave: hideTooltip,
        },
        click: {
          ...defaultEvents,
          onClick: toggleTooltip,
        },
      };
    }, [defaultEvents, hideTooltip, showTooltip, toggleTooltip]);

    useImperativeHandle(ref, () => content.current);

    useEffect(() => {
      if (typeof show !== 'boolean') {
        updateTooltip();
      }
      if (typeof show === 'boolean' && show) {
        showTooltip();
      }
      if (show === false) {
        hideTooltip();
      }
    }, [updateTooltip, show, showTooltip, hideTooltip, events, event]);

    useEffect(() => {
      window.addEventListener('resize', updateTooltip);

      return () => window.removeEventListener('resize', updateTooltip);
    }, [events, event, updateTooltip]);

    return (
      <>
        <span
          data-testid={`${dataTestId}-clickable`}
          ref={content}
          className={classNames(className, {
            [styles['clickable']]: event === 'click',
          })}
          {...events[event]}
          {...rest}>
          {children}
        </span>
        <div
          data-testid={dataTestId}
          data-component-name={COMPONENT_ID}
          data-id={dataTestId}
          ref={tooltip}
          style={{
            maxWidth: `${maxWidth || DEFAULT_MAX_WIDTH}px`,
            ...style,
          }}
          className={styles['tooltip']}
          role="tooltip"
          title="">
          {(heading || title) && (
            <Typography
              className={styles['title']}
              variant="body-semibold"
              dataTestId={`${dataTestId}-title`}>
              {cropText(heading || title, maxTitleLength)}
            </Typography>
          )}
          {text && (
            <Typography
              className={styles['text']}
              variant="caption-1-regular"
              dataTestId={`${dataTestId}-text`}>
              {cropText(text, maxTextLength)}
            </Typography>
          )}
          <span className={styles['btn-block']} data-testid={`${dataTestId}-button`}>
            {button}
          </span>
          <div ref={arrowElem} data-testid={`${dataTestId}-arrow`} />
        </div>
      </>
    );
  },
);

Tooltip.displayName = 'Tooltip';
